import './styles/ModuleSelections.scss';

import { useApolloClient, useMutation, useQuery as useGQLQuery } from '@apollo/client';
import { CreateNewFolder, Edit, Email } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Icon,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import SEND_EMAIL_NOTIFICATION from 'gql/mutations/ModuleSelectionsService/sendEmailNotificationForSelection.gql';
import QUERY from 'gql/queries/ModulesService/getStatesModuleSelections.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { DateBox, EntryWithHeadline } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Filter } from 'app/components/Filters';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { TilesOrListView, TypedGridColumns } from 'app/components/TilesOrListView';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { SortOption } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';
import useQueryStringState from 'utils/hooks/useQueryStringState';

import { SelectionTabPanels } from '../ModuleSelectionDetail';
import ModuleSelectionModal from '../ModuleSelectionModal/ModuleSelectionModal';
import { hasAtLeastStarted } from '../helper';
import {
  Attachment,
  Maybe,
  ModuleSelection as ModuleSelectionType,
  Observer,
  State,
  SubjectType
} from '../models';

export class ModuleSelection implements ModuleSelectionType {
  public id: string;
  public subjectType: SubjectType;
  public title: string;
  public generalNotes?: string;
  public electionTip?: string;
  public attachments?: Maybe<Attachment[]>;
  public startDate?: Date;
  public endDate?: Date;
  public complete?: boolean;
  public contentVisibleFrom?: Date;
  public dischargeByStudentsUntil?: Date;
  public lengthOfTheWishlist?: number;
  public maxSelectedModules?: number;
  public activeInAllCommunities?: boolean;
  public state: State;
  public createdByCourseId?: number;
  public participatingCourseIds?: Maybe<number[]>;
  public courseId?: number;
  public studentsAlreadyNotified?: boolean;
  public publishedAt?: Date | undefined;
  public observers: Observer[];
  constructor({
    id,
    subjectType,
    title,
    generalNotes,
    electionTip,
    attachments,
    startDate,
    endDate,
    contentVisibleFrom,
    dischargeByStudentsUntil,
    lengthOfTheWishlist,
    maxSelectedModules,
    activeInAllCommunities,
    state,
    createdByCourseId,
    participatingCourseIds,
    courseId,
    studentsAlreadyNotified,
    publishedAt,
    observers
  }: ModuleSelectionType) {
    this.id = id;
    this.subjectType = subjectType;
    this.title = title;
    this.generalNotes = generalNotes;
    this.electionTip = electionTip;
    this.attachments = attachments;
    this.lengthOfTheWishlist = lengthOfTheWishlist;
    this.maxSelectedModules = maxSelectedModules;
    this.activeInAllCommunities = activeInAllCommunities;
    this.state = state;
    this.startDate = startDate;
    this.dischargeByStudentsUntil = dischargeByStudentsUntil;
    this.endDate = endDate;
    this.contentVisibleFrom = contentVisibleFrom;
    this.createdByCourseId = createdByCourseId;
    this.participatingCourseIds = participatingCourseIds;
    this.courseId = courseId;
    this.studentsAlreadyNotified = studentsAlreadyNotified;
    this.publishedAt = publishedAt;
    this.observers = observers;
  }
}

export class ModuleSelectionWithCourses extends ModuleSelection {
  public createdByCourse: string;
  constructor({
    id,
    subjectType,
    title,
    generalNotes,
    electionTip,
    attachments,
    startDate,
    endDate,
    contentVisibleFrom,
    dischargeByStudentsUntil,
    lengthOfTheWishlist,
    maxSelectedModules,
    activeInAllCommunities,
    state,
    createdByCourseId,
    participatingCourseIds,
    courseId,
    createdByCourse,
    studentsAlreadyNotified,
    publishedAt,
    observers
  }) {
    super({
      id,
      subjectType,
      title,
      generalNotes,
      electionTip,
      attachments,
      startDate,
      endDate,
      contentVisibleFrom,
      dischargeByStudentsUntil,
      lengthOfTheWishlist,
      maxSelectedModules,
      activeInAllCommunities,
      state,
      createdByCourseId,
      participatingCourseIds,
      courseId,
      studentsAlreadyNotified,
      publishedAt,
      observers
    });
    this.createdByCourse = createdByCourse;
  }
}

interface ModalContextProps {
  openModal: (editingModuleSelection?: ModuleSelection) => void;
  openTemplateModal: (templateModuleSelection: ModuleSelection) => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

interface Props {
  subjectType: SubjectType;
}

enum SelectionsTabPanels {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ARCHIVE = 'archive',
  DRAFT = 'draft'
}

const statesForTab = {
  [SelectionsTabPanels.ACTIVE]: [State.Active, State.Completed, State.Dischargable, State.Started],
  [SelectionsTabPanels.INACTIVE]: [State.Inactive],
  [SelectionsTabPanels.ARCHIVE]: [State.Archived],
  [SelectionsTabPanels.DRAFT]: [State.Draft]
};

export const ModuleSelections = (props: Props) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { currentUser } = client.readQuery({ query: USER_QUERY });
  const aclHelper = new ACLHelper(currentUser);
  const [tab, setTab] = useQueryStringState<SelectionsTabPanels>('tab', SelectionsTabPanels.ACTIVE);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [createFromTemplate, setCreateFromTemplate] = useState(false);
  const [currentlyEditingModuleSelection, setCurrentlyEditingModuleSelection] = useState<
    ModuleSelection | undefined
  >(undefined);
  const searchInputState = useState('');
  const startDateInputState = useState(null);
  const endDateInputState = useState(null);
  const courseOfStudyState = useState([]);

  usePageHeading({
    heading: t(translations.pages.moduleSelections.title[props.subjectType.toLowerCase()]()),
    mobilePadding: false
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentURL = useCurrentURL();
  const modalContextValue = useMemo(
    () => ({
      openModal: (editingModuleSelection?: ModuleSelection) => {
        if (editingModuleSelection) {
          setCurrentlyEditingModuleSelection(editingModuleSelection);
        }
        setCreateFromTemplate(false);
        setCreateModalOpen(true);
      },
      openTemplateModal: (templateModuleSelection: ModuleSelection) => {
        setCurrentlyEditingModuleSelection(templateModuleSelection);
        setCreateFromTemplate(true);
        setCreateModalOpen(true);
      },
      confirmDelete: () => {
        setConfirmDeleteModalOpen(true);
      }
    }),
    [setCurrentlyEditingModuleSelection, setCreateModalOpen]
  );

  const { data: courses } = useCoursesQuery();

  const isLecturer = !aclHelper.isSuperUser() && !aclHelper.isSelectionAdmin(true);

  const isSubjectAdmin = aclHelper.isSubjectSelectionAdmin(false, props.subjectType);

  function isAdminOfSelection(courses, moduleSelection) {
    return (
      (aclHelper.isSelectionAdmin(true) && props.subjectType === SubjectType.Fwpm
        ? aclHelper
            .getSelectionAdminCourses(courses)
            .map(course => course.id)
            .includes(moduleSelection.courseId)
        : aclHelper.isSubjectSelectionAdmin(true, SubjectType.Awpm)) || aclHelper.isSuperUser()
    );
  }
  const getModuleSelectionsQuery = useGQLQuery<any, any>(QUERY, {
    context: { clientName: 'federation' },
    variables: {
      states: statesForTab[tab],
      subjectType: props.subjectType,
      courses:
        !aclHelper.isSuperUser() && props.subjectType === SubjectType.Fwpm
          ? aclHelper.getSelectionAdminCourses(courses)?.map(course => course.id)
          : courses?.map(course => course.id),
      lecturerId: isLecturer ? currentUser.id.toString() : null,
      selectionAdmin: aclHelper.isSelectionAdmin()
    },
    fetchPolicy: 'no-cache'
  });
  const { refetch } = getModuleSelectionsQuery;

  const handleTabChange = useCallback(
    (event: React.ChangeEvent<{}>, value: SelectionsTabPanels) => {
      searchInputState[1]('');
      startDateInputState[1](null);
      endDateInputState[1](null);
      courseOfStudyState[1]([]);
      setTab(value);
    },
    [courseOfStudyState, endDateInputState, searchInputState, setTab, startDateInputState]
  );

  const cancelModuleSelectionCreation = (event?, reason?) => {
    if (disabledCancel || (reason && reason === 'backdropClick')) {
      return;
    }
    setCurrentlyEditingModuleSelection(undefined);
    setDisabledCancel(true);
    setCreateModalOpen(false);
    setDisabledCancel(false);
  };

  const richlistFilter: Filter[] = [
    {
      field: 'name',
      label: t(translations.pages.moduleSelections.filter.search()),
      stateHandler: searchInputState
    },
    {
      field: 'startDate',
      label: t(translations.pages.moduleSelections.filter.startDate()),
      datePicker: { startTimeAtDay: false, stateHandler: startDateInputState }
    },
    {
      field: 'endDate',
      label: t(translations.pages.moduleSelections.filter.endDate()),
      datePicker: { startTimeAtDay: false, stateHandler: endDateInputState }
    },
    {
      field: 'courseOfStudy',
      label: t(translations.pages.moduleSelections.filter.courseOfStudy()),
      multiselect: {
        options: courses
          .map(course => ({
            label: `(${course.shorthand}) ${course.name}`,
            name: course.id
          }))
          .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1)),
        customStyle: { width: '200px' },
        stateHandler: courseOfStudyState
      },
      quickInfo: t(translations.pages.moduleSelections.filter.courseOfStudyQuickInfo())
    },
    {
      field: 'userScope',
      toggle: {
        value: 'self',
        color: 'archived'
      },
      label: t(translations.pages.moduleSelections.filter.mySelections())
    }
  ];

  const handleFilterChange = variables => {
    const parsedFilter = JSON.parse(variables.filter);

    const modifiedFilter: Record<string, any> = {
      name: undefined,
      startDate: undefined,
      endDate: undefined,
      courseOfStudy: undefined
    };

    Object.keys(parsedFilter).forEach(key => {
      modifiedFilter[key] = parsedFilter[key].term;
    });

    modifiedFilter.userScope = parsedFilter.userScope ? 'self' : 'all';

    refetch(modifiedFilter);
  };

  const sortOptions: SortOption[] = [
    {
      label: t(translations.pages.moduleSelections.sort.nameAsc()),
      value: { titleSort: 'asc' }
    },
    {
      label: t(translations.pages.moduleSelections.sort.nameDesc()),
      value: { titleSort: 'desc' }
    }
  ];

  const onSortChange = sortings => {
    refetch(sortings);
  };

  const rowActions = useCallback(
    (params: GridRowParams<ModuleSelection>) => {
      const templateActions =
        tab !== SelectionsTabPanels.ACTIVE && tab !== SelectionsTabPanels.INACTIVE;

      const actions: JSX.Element[] = [];

      if (!templateActions) {
        actions.push(
          <GridActionsCellItem
            icon={
              <Icon
                className="fa fa-pen"
                style={{
                  width: 'auto',
                  color: 'lightblue'
                }}
              />
            }
            onClick={() => modalContextValue?.openModal(params.row)}
            label={t(translations.common.edit())}
            showInMenu
          />
        );
      } else {
        actions.push(
          <GridActionsCellItem
            icon={
              <CreateNewFolder
                style={{
                  width: 'auto',
                  color: 'lightblue'
                }}
              />
            }
            label={t(translations.pages.moduleSelections.useAsTemplate())}
            onClick={() => modalContextValue?.openTemplateModal(params.row)}
            showInMenu
          />
        );
      }
      return actions;
    },
    [t, modalContextValue, tab]
  );

  const columns = useMemo((): TypedGridColumns<ModuleSelectionWithCourses> => {
    return [
      {
        field: 'title',
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.moduleSelections.create.generalInfo.title())
      },
      {
        field: 'startDate',
        flex: 2,
        headerName: t(translations.pages.moduleSelections.create.periodsAndExtraInfo.startDate()),
        valueGetter: (value, row) =>
          row.startDate
            ? new Date(row.startDate).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })
            : '-/-'
      },
      {
        field: 'endDate',
        flex: 1,
        headerName: t(translations.pages.moduleSelections.create.periodsAndExtraInfo.endDate()),
        valueGetter: (value, row) =>
          row.endDate
            ? new Date(row.endDate).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })
            : '-/-'
      },
      {
        field: 'dischargeByStudentsUntil',
        flex: 2,
        headerName: t(
          translations.pages.moduleSelections.create.periodsAndExtraInfo.dischargeByStudentsUntil()
        ),
        valueGetter: (value, row) =>
          row.dischargeByStudentsUntil
            ? new Date(row.dischargeByStudentsUntil).toLocaleString('de-DE', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : '-/-'
      },
      {
        field: 'createdByCourse',
        valueGetter: (value, row) => row.createdByCourse,
        flex: 3,
        headerName: t(translations.pages.moduleSelections.createdBy())
      },
      {
        field: 'participatingCourseIds',
        valueGetter: (value, row) => row.participatingCourseIds?.join(', '),
        flex: 3,
        headerName: t(translations.pages.moduleSelections.participants())
      },
      {
        field: 'actions',
        flex: 1,
        headerName: t(translations.common.actions()),
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  return (
    <ModalContext.Provider value={modalContextValue}>
      <ErrorBoundary boundaryTag="createModuleSelectionsBoundary">
        <Modal
          open={createModalOpen}
          onClose={(_event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setCancelModalOpen(true);
          }}
          headline={
            !!currentlyEditingModuleSelection
              ? createFromTemplate
                ? t(translations.pages.moduleSelections.createModalTemplateTitle())
                : t(translations.pages.moduleSelections.editModalTitle())
              : t(
                  translations.pages.moduleSelections.createModalTitle[
                    props.subjectType.toLowerCase()
                  ]()
                )
          }
          onCancel={(_event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setCancelModalOpen(true);
          }}
        >
          <ModuleSelectionModal
            setDisabledCancel={setDisabledCancel}
            closeHandler={cancelModuleSelectionCreation}
            moduleSelectionToUpdate={currentlyEditingModuleSelection}
            fromTemplate={createFromTemplate}
            subjectType={props.subjectType}
            refetchQueries={['getStatesModuleSelections']}
          />
        </Modal>
      </ErrorBoundary>
      <ErrorBoundary boundaryTag="deleteModule">
        <ConfirmationDialog
          title={t(translations.pages.moduleSelectionsDetail.modulePreview.deleteModuleSelection())}
          open={confirmDeleteModalOpen}
          onClose={() => {
            setConfirmDeleteModalOpen(false);
          }}
          onConfirm={() => {
            //TODO HERE IMPLEMENT DELETE
            setConfirmDeleteModalOpen(false);
            window.alert('TODO');
          }}
        >
          {t(
            translations.pages.moduleSelectionsDetail.modulePreview.deleteModuleSelectionDescription()
          )}
        </ConfirmationDialog>
        <ConfirmationDialog
          open={cancelModalOpen}
          title={t(translations.common.cancelConfirm.title())}
          onClose={() => {
            setCancelModalOpen(false);
          }}
          onConfirm={() => {
            setCreateModalOpen(false);
            setCreateFromTemplate(false);
            setCancelModalOpen(false);
          }}
        >
          <div>{t(translations.common.cancelConfirm.message())}</div>
        </ConfirmationDialog>
      </ErrorBoundary>
      <Box display="flex" flexDirection="column">
        {isSubjectAdmin && (
          <Button
            startIcon={<Icon className="fas fa-plus" style={{ color: 'white' }} />}
            variant="contained"
            color="primary"
            onClick={() => {
              setCreateModalOpen(true);
            }}
            autoCapitalize="none"
            className="add-module-selection"
          >
            {t(translations.pages.moduleSelections.createSelection())}
          </Button>
        )}
        <Paper>
          <Grid container className="module-selections">
            {aclHelper.isSelectionAdmin() && (
              <Tabs
                value={tab}
                onChange={handleTabChange}
                aria-label="active/inactive tabs"
                indicatorColor="primary"
              >
                {Object.keys(SelectionsTabPanels).map(tab => (
                  <Tab
                    value={tab.toLowerCase()}
                    key={tab.toLowerCase()}
                    label={t(translations.pages.moduleSelections[tab.toLowerCase() + 'Tab']())}
                  />
                ))}
              </Tabs>
            )}

            <ErrorBoundary boundaryTag="moduleSelectionsListBoundary">
              <GQLGuard
                query={[getModuleSelectionsQuery]}
                onError={error => {
                  if (error.graphQLErrors[0]?.extensions?.status === 404) {
                    dispatch(
                      AlertActions.error(
                        `${t(translations.pages.moduleSelections.failedLoading())}`
                      )
                    );
                  } else {
                    dispatch(
                      AlertActions.error(
                        `${t(translations.pages.moduleSelections.failedLoading())}: ${
                          error.message
                        }`
                      )
                    );
                  }
                  navigate('/', { replace: true });
                  return null;
                }}
              >
                {([data]) => {
                  const moduleSelections: ModuleSelectionWithCourses[] =
                    data?.getStatesModuleSelections?.map(moduleSelection => {
                      return new ModuleSelectionWithCourses({
                        ...moduleSelection,
                        createdByCourse:
                          props.subjectType === SubjectType.Fwpm
                            ? courses?.find(item => item.id === moduleSelection.courseId)?.name
                            : t(translations.pages.moduleSelections.global()),
                        participatingCourseIds: courses
                          ?.filter(course =>
                            moduleSelection.participatingCourseIds?.includes(course.id)
                          )
                          .map(filteredCourse => filteredCourse.shorthand)
                      });
                    });

                  return (
                    <Grid style={{ width: '100%', margin: '1rem' }}>
                      <TilesOrListView
                        data={moduleSelections}
                        columns={
                          aclHelper.isSuperUser()
                            ? columns
                            : columns?.filter((_, index) => index < columns.length - 1)
                        }
                        height={'70vh'}
                        onFilterChange={handleFilterChange}
                        filters={richlistFilter}
                        sortOptions={sortOptions}
                        onSortChange={onSortChange}
                        renderTile={item => (
                          <ModuleSelectionCard
                            moduleSelection={item}
                            subjectType={props.subjectType}
                            hideButtons={!isAdminOfSelection(courses, item)}
                          />
                        )}
                        onRowClick={event => {
                          navigate(
                            `/${event.row.subjectType.toLowerCase()}/module-selections/${
                              event.row.id
                            }`,
                            { state: { back: currentURL } }
                          );
                        }}
                      />
                    </Grid>
                  );
                }}
              </GQLGuard>
            </ErrorBoundary>
          </Grid>
        </Paper>
      </Box>
    </ModalContext.Provider>
  );
};

export const ModuleSelectionCard = ({
  moduleSelection,
  subjectType,
  isStudentView = false,
  hideButtons
}: {
  moduleSelection: ModuleSelectionWithCourses;
  subjectType: SubjectType;
  isStudentView?: boolean;
  hideButtons?: boolean;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentURL = useCurrentURL();
  const modalContext = useContext(ModalContext);
  const { t } = useTranslation();
  const [confirmSendEmailDialogOpen, setConfirmSendEmailDialogOpen] = useState(false);
  const [sendEmailNotification] = useMutation(SEND_EMAIL_NOTIFICATION, {
    context: { clientName: 'federation' }
  });
  const directTo: string = !isStudentView
    ? `/${subjectType.toLowerCase()}/module-selections/${moduleSelection.id}`
    : `/${subjectType.toLowerCase()}/student/module-selections/${moduleSelection.id}`;

  const handleSendEmailNotificationConfirm = async () => {
    try {
      await sendEmailNotification({
        variables: { id: parseInt(moduleSelection.id, 10) },
        refetchQueries: ['getModuleSelectionById']
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.moduleSelectionsDetail.modulePreview.success.sendEmailNotification())
        )
      );
    } catch (e) {
      dispatch(
        AlertActions.error(
          t(translations.pages.moduleSelectionsDetail.modulePreview.error.sendEmailNotification())
        )
      );
    }
  };

  return (
    <>
      <ConfirmationDialog
        open={confirmSendEmailDialogOpen}
        onClose={() => setConfirmSendEmailDialogOpen(false)}
        title={t(
          translations.pages.moduleSelectionsDetail.modulePreview.confirm.startEmailNotificationTitle(),
          {
            title: moduleSelection.title
          }
        )}
        onConfirm={() => {
          setConfirmSendEmailDialogOpen(false);
          handleSendEmailNotificationConfirm();
        }}
      >
        {t(
          translations.pages.moduleSelectionsDetail.modulePreview.confirm.startEmailNotificationBody()
        )}
      </ConfirmationDialog>
      <Card
        variant="elevation"
        className={`selection-card ${
          !hideButtons
            ? moduleSelection.state.toLocaleLowerCase()
            : 'selection-card__studentViewContent'
        }`}
        sx={{ height: '100%' }}
      >
        <div className="selection-card__content">
          <CardHeader
            onClick={() => navigate(directTo, { state: { back: currentURL } })}
            title={
              <div>
                <Typography component="h2">{moduleSelection.title}</Typography>
                <DateBox startDate={moduleSelection.startDate} endDate={moduleSelection.endDate} />
              </div>
            }
          />

          <Divider />
          <CardContent onClick={() => navigate(directTo, { state: { back: currentURL } })}>
            <EntryWithHeadline
              headline={t(translations.pages.moduleSelections.dischargeStudentsUntil())}
              entry={`Bis ${
                moduleSelection.dischargeByStudentsUntil
                  ? new Date(moduleSelection.dischargeByStudentsUntil).toLocaleString?.('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : '-/-'
              }`}
            />
            <EntryWithHeadline
              headline={t(
                translations.pages.moduleSelections.create.periodsAndExtraInfo.contentVisibleFrom()
              )}
              entry={`${
                moduleSelection.contentVisibleFrom
                  ? new Date(moduleSelection.contentVisibleFrom).toLocaleString?.('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : '-/-'
              }`}
            />
            {subjectType === SubjectType.Fwpm && (
              <EntryWithHeadline
                headline={t(translations.pages.moduleSelections.createdBy())}
                entry={`${moduleSelection.createdByCourse}`}
                bold
              />
            )}
            {!!moduleSelection.participatingCourseIds?.length && (
              <EntryWithHeadline
                headline={t(translations.pages.moduleSelections.participants())}
                entry={moduleSelection.participatingCourseIds?.join(', ')}
              />
            )}
          </CardContent>
        </div>
        {!hideButtons && (
          <div className="selection-card__actions">
            {statesForTab[SelectionsTabPanels.ACTIVE].includes(moduleSelection.state) ||
            statesForTab[SelectionsTabPanels.INACTIVE].includes(moduleSelection.state) ? (
              <>
                <button onClick={() => modalContext?.openModal(moduleSelection)}>
                  <Edit />
                  {t(translations.common.edit())}
                </button>
                <button
                  disabled={!moduleSelection.publishedAt || moduleSelection.studentsAlreadyNotified}
                  onClick={() => setConfirmSendEmailDialogOpen(true)}
                >
                  <Email />
                  {t(translations.pages.moduleSelections.startEmailNotification())}
                </button>
                <button
                  disabled={!hasAtLeastStarted(moduleSelection)}
                  onClick={() =>
                    navigate({
                      pathname: `/${subjectType.toLowerCase()}/module-selections/${
                        moduleSelection.id
                      }`,
                      search: `?tab=${SelectionTabPanels.OCCUPANCY}`
                    })
                  }
                >
                  <Icon className="fas fa-list" />
                  {t(translations.pages.moduleSelections.viewOccupancy())}
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  modalContext?.openTemplateModal(moduleSelection);
                }}
              >
                <CreateNewFolder />
                {t(translations.pages.moduleSelections.useAsTemplate())}
              </button>
            )}
          </div>
        )}
      </Card>
    </>
  );
};
